import { useTranslation } from "react-i18next";
import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";

const RED = "#C81400";
const BLACK = "#000000";
const CREAM = "#F5F0E8";
const CREAM_70 = "rgba(245,240,232,0.7)";
const CREAM_10 = "rgba(245,240,232,0.1)";

const STEPS = [1, 2, 3, 4, 5] as const;

// One step. As it crosses the middle of the viewport it grows slightly and picks up the
// red highlight, then settles back once it has passed. Reduced motion gets the static look.
function Step({ n }: { n: number }) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const ref = useRef<HTMLLIElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });

  // progress 0.5 = the item's centre is at the viewport's centre
  const stops = [0, 0.3, 0.5, 0.7, 1];
  const scale = useTransform(scrollYProgress, stops, [0.95, 0.98, 1.04, 0.98, 0.95]);
  const background = useTransform(scrollYProgress, stops, ["#111111", "#111111", "#2a0a06", "#111111", "#111111"]);
  const border = useTransform(scrollYProgress, stops, [CREAM_10, CREAM_10, RED, CREAM_10, CREAM_10]);

  return (
    <motion.li
      ref={ref}
      className="flex gap-4 sm:gap-6 items-start rounded-2xl p-5 sm:p-6"
      style={
        reduce
          ? { backgroundColor: "#111111", border: `1px solid ${CREAM_10}` }
          : { scale, backgroundColor: background, borderWidth: 1, borderStyle: "solid", borderColor: border }
      }
    >
      <span
        className="shrink-0 w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-xl sm:text-2xl"
        style={{ backgroundColor: RED, color: CREAM, fontFamily: "var(--font-display)" }}
        aria-hidden="true"
      >
        {n}
      </span>
      <div>
        <h3 className="text-xl sm:text-2xl mb-1" style={{ color: CREAM, fontFamily: "var(--font-display)" }}>
          {t(`howNightPlays.step${n}.title`)}
        </h3>
        <p className="text-base sm:text-lg" style={{ color: CREAM_70, fontFamily: "var(--font-body)" }}>
          {t(`howNightPlays.step${n}.description`)}
        </p>
      </div>
    </motion.li>
  );
}

// "How a night plays": what the guests do on the night, as opposed to
// HorizontalHowItWorks, which explains how the host creates the mystery.
export default function HowANightPlays() {
  const { t } = useTranslation();

  return (
    <section
      className="py-16 sm:py-24 px-4 sm:px-6"
      style={{ backgroundColor: BLACK, borderTop: `1px solid ${CREAM_10}` }}
      aria-labelledby="how-a-night-plays-title"
    >
      <div className="max-w-5xl mx-auto">
        <h2
          id="how-a-night-plays-title"
          className="text-3xl sm:text-4xl md:text-5xl text-center mb-4"
          style={{ color: CREAM, fontFamily: "var(--font-display)" }}
        >
          {t("howNightPlays.title")}
        </h2>
        <p
          className="text-center text-base sm:text-lg max-w-2xl mx-auto mb-12 sm:mb-16"
          style={{ color: CREAM_70, fontFamily: "var(--font-body)" }}
        >
          {t("howNightPlays.subtitle")}
        </p>

        <ol className="space-y-4 sm:space-y-5">
          {STEPS.map((n) => (
            <Step key={n} n={n} />
          ))}
        </ol>
      </div>
    </section>
  );
}
